/**
 * The DRAWING behind a `QuestionGlyphKey` — one table, two surfaces.
 *
 * `model/questionGlyphs` decides which mark a predefined question wears; this maps that decision to
 * a component, and both the editor's standard rows and the Ankiety card's rows read it, so the two
 * cannot come to disagree about what „Sen" looks like.
 *
 * A `Record` over the whole key union rather than a `switch`: a mark added to the union without a
 * drawing is then a compile error rather than a row that silently draws nothing.
 *
 * Eleven of the entries point at glyphs `AnswerTypePill` already draws. That is the fallback doing
 * its job — an unrecognised code wears the mark its own type pill wears — and not a shortcut: a
 * second moon drawn for this column would be the near-duplicate the styling rules exist to stop.
 */

import { CalendarIcon } from '~/shared/ui/AuthIcons';

import type { QuestionGlyphKey } from '../model/questionGlyphs';
import {
  ActivityIcon,
  BandageIcon,
  BoltIcon,
  CakeIcon,
  ClipIcon,
  DumbbellIcon,
  HeartIcon,
  HeightIcon,
  LongTextIcon,
  MedalIcon,
  MoonIcon,
  MultiChoiceIcon,
  NumberIcon,
  PersonIcon,
  PhotoIcon,
  PillIcon,
  RulerIcon,
  ScaleIcon,
  ShortTextIcon,
  SingleChoiceIcon,
  TargetIcon,
  UtensilsIcon,
  WeightIcon,
} from '~/shared/ui/ReportIcons';

type GlyphComponent = (props: { readonly className?: string }) => React.JSX.Element;

const GLYPHS: Record<QuestionGlyphKey, GlyphComponent> = {
  cake: CakeIcon,
  person: PersonIcon,
  height: HeightIcon,
  weight: WeightIcon,
  bandage: BandageIcon,
  heart: HeartIcon,
  pill: PillIcon,
  activity: ActivityIcon,
  target: TargetIcon,
  medal: MedalIcon,
  calendar: CalendarIcon,
  dumbbell: DumbbellIcon,
  utensils: UtensilsIcon,
  moon: MoonIcon,
  bolt: BoltIcon,
  ruler: RulerIcon,
  camera: PhotoIcon,
  shortText: ShortTextIcon,
  longText: LongTextIcon,
  singleChoice: SingleChoiceIcon,
  multiChoice: MultiChoiceIcon,
  number: NumberIcon,
  scale: ScaleIcon,
  clip: ClipIcon,
};

export interface QuestionGlyphProps {
  /** `null` for a question the trainer wrote themselves — nothing is drawn. */
  readonly glyph: QuestionGlyphKey | null;
  readonly className?: string;
}

export function QuestionGlyph({ glyph, className }: QuestionGlyphProps) {
  if (glyph === null) return null;
  const Mark = GLYPHS[glyph];
  return <Mark className={className} />;
}
